/**
 * 적체·장애 배너 — §7.3 L1/L2/L3 와 §7.4 model_down.
 *
 * 설계의 3단계 조치를 사용자 언어로 번역해 보여준다. 각 단계가 무엇을
 * "하고 있는지"까지 말해야 호스트가 말하는 속도를 조절할 수 있다.
 */

import { Alert, AlertDescription, AlertTitle } from '@cupia/ui/alert'
import { AlertTriangle, Layers, MicOff, ServerCrash, Trash2, WifiOff } from 'lucide-react'
import type { UpstreamNotice } from '../lib/events'
import type { RoomView } from '../lib/reducer'
import type { ConnState } from '../lib/transport'

/** `room.status.upstream_notice` → 문구(TASK_agw_client §3 사용자 안내). */
const UPSTREAM_NOTICE_TEXT: Record<UpstreamNotice, { title: string; body: string }> = {
  busy: {
    title: '통역 서버가 바쁩니다',
    body: '동시 처리 상한에 걸려 잠시 대기 후 재시도하고 있습니다. 자막이 몇 초 늦게 나올 수 있습니다.',
  },
  down: {
    title: '통역 서버 일시 중단',
    body: '상류 모델이 응답하지 않습니다(503). 자동으로 재시도합니다 — 계속되면 운영팀에 알려 주세요.',
  },
  timeout: {
    title: '통역 서버 응답이 늦습니다',
    body: '요청이 시간 안에 끝나지 않았습니다(504/예산 초과). 일부 발화가 자막에서 빠질 수 있습니다.',
  },
}

export function StatusAlerts({
  room,
  conn,
  viewerRole = 'viewer',
  lastError = null,
  credentialDead = false,
}: {
  room: RoomView
  conn: ConnState
  /**
   * DEF-018 §5.3 — 토큰 재교환이 403/404 를 **연속 2회** 받았다.
   *
   * "종료" 라고 말하지 않는다: 403 두 번은 **자격이 거부됐다**는 증거이지
   * **회의가 끝났다**는 증거가 아니다. 서버는 살아 있는 방에 대해서도
   * 소유 불일치면 403 을 돌려준다.
   */
  credentialDead?: boolean
  /** §7.4 `caption.error` 로 표면화된 마지막 오류(A2 무음 경고 포함). */
  lastError?: { reason: string; ts: number } | null
  /** 호스트 콘솔이면 안내 문구가 달라진다(새 방을 열 수 있는 쪽).
   *  이름이 `role` 이 아닌 이유: ARIA 예약어라 DOM role 로 오인된다. */
  viewerRole?: 'host' | 'viewer'
}): React.ReactElement | null {
  // ── 종료 **증거**가 있을 때만 "종료" 라고 말한다(DEF-018 §5.3) ────
  // 참가자 전용 `/liveness` 가 200 으로 state:'closed' 를 명시 반환한 경우다. 아래
  // `room_gone`(재접속 소진·판정 불가)과 문구가 달라야 한다 — 서버가
  // 아는 것 이상을 화면이 단정하면 사용자가 살아 있는 회의를 포기한다.
  if (conn === 'room_closed') {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>회의가 종료됐습니다</AlertTitle>
        <AlertDescription>
          회의가 종료됐습니다. 다시 참가하려면 패스코드로 재입장해 주세요.
        </AlertDescription>
      </Alert>
    )
  }

  // 자격 종결(§1.2 403/404 연속 2회) — "종료" 가 아니라 "자격" 을 말한다.
  if (credentialDead) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>입장 자격이 만료되었습니다</AlertTitle>
        <AlertDescription>
          입장 자격이 더 이상 유효하지 않습니다. 패스코드로 다시 입장해 주세요.
        </AlertDescription>
      </Alert>
    )
  }

  // 방이 사라진 경우가 가장 시급하다 — 재시도해도 살아나지 않는다.
  // **재접속 소진·판정 불가 전용**이다(DEF-018 §5.3): 여기서 "종료" 를
  // 단정하지 않고 현행 연결 실패 계열 문구를 유지한다.
  if (conn === 'room_gone') {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>
          {viewerRole === 'host' ? '방이 종료되었습니다' : '회의가 종료되었습니다'}
        </AlertTitle>
        <AlertDescription>
          {viewerRole === 'host'
            ? '이 방은 더 이상 존재하지 않습니다. 새 방을 만들어 주세요.'
            : '호스트가 방을 닫았거나 회의가 끝났습니다. 새 코드로 다시 입장해 주세요.'}
        </AlertDescription>
      </Alert>
    )
  }

  // 자격 종결(§5.2) — 토큰 재교환까지 실패해 더 붙을 자격이 없다.
  // 재시도 버튼을 주지 않는 이유: transport 가 이미 재교환·재시도를 다
  // 써 버린 상태다. 남은 길은 새 자격(재입장·재로그인)뿐이고, 그걸 말한다.
  // 이 배너가 없으면 "조용히 죽은 화면" 이 된다 — 자막만 멈추고 아무도
  // 이유를 모른다.
  if (conn === 'terminated') {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>접속 자격이 만료되었습니다</AlertTitle>
        <AlertDescription>
          {viewerRole === 'host'
            ? '자격 갱신에 실패해 연결이 종료되었습니다. 첫 화면에서 다시 로그인한 뒤 입장해 주세요.'
            : '자격 갱신에 실패해 연결이 종료되었습니다. 6자리 코드로 다시 입장해 주세요.'}
        </AlertDescription>
      </Alert>
    )
  }

  // 연결 자체가 끊긴 경우가 그다음이다.
  if (conn === 'reconnecting') {
    return (
      <Alert variant="warning">
        <WifiOff className="h-4 w-4" />
        <AlertTitle>재접속 중</AlertTitle>
        <AlertDescription>
          연결이 끊겨 재시도하고 있습니다. 복구되면 놓친 자막을 이어서 받습니다.
        </AlertDescription>
      </Alert>
    )
  }

  if (conn === 'replaced') {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>다른 곳에서 접속했습니다</AlertTitle>
        <AlertDescription>
          같은 호스트 키로 새 연결이 열려 이 세션이 대체되었습니다.
        </AlertDescription>
      </Alert>
    )
  }

  // ── A2: 서버가 소리를 못 받고 있다 ──────────────────────────────────
  //
  // **모델 중단보다 먼저 본다**: 소리가 안 올라가면 모델이 멀쩡해도 자막은
  // 안 나오고, 호스트가 조치할 수 있는 것은 이쪽뿐이다(마이크·권한·장치).
  // 실측 사고에서 화면 레벨 미터는 정상으로 보였는데 서버에는 무음만
  // 갔다 — 그래서 판정 근거는 **서버가 받은 바이트**여야 한다.
  if (viewerRole === 'host' && lastError?.reason === 'audio_silent') {
    return (
      <Alert variant="destructive">
        <MicOff className="h-4 w-4" />
        <AlertTitle>서버가 소리를 받지 못하고 있습니다</AlertTitle>
        <AlertDescription>
          30초 넘게 무음만 도착했습니다. 마이크 음소거·입력 장치·브라우저 권한을
          확인해 주세요. 화면의 레벨 미터가 움직여도 서버에는 소리가 닿지 않을 수
          있습니다.
        </AlertDescription>
      </Alert>
    )
  }

  // §7.4 서킷 오픈
  if (room.state === 'model_down') {
    return (
      <Alert variant="destructive">
        <ServerCrash className="h-4 w-4" />
        <AlertTitle>통역 모델 응답 중단</AlertTitle>
        <AlertDescription>
          연속 실패로 회로가 열렸습니다. 30초 후 자동으로 재시도합니다 — 그동안 발화는
          자막으로 나오지 않습니다.
        </AlertDescription>
      </Alert>
    )
  }

  // ── 게이트웨이 안내(TASK_agw_client §3, 호스트 전용) ──────────────
  // 회로는 아직 닫혀 있지만 상류가 최근 429/503/504 를 돌려줬다 — 자막은
  // 예산 안에서 재시도되므로 "중단" 이 아니라 "느려질 수 있음" 이다. 기존
  // 경고 배너 변형을 그대로 쓴다(새 UI 최소).
  if (viewerRole === 'host' && room.upstreamNotice) {
    const text = UPSTREAM_NOTICE_TEXT[room.upstreamNotice]
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>{text.title}</AlertTitle>
        <AlertDescription>{text.body}</AlertDescription>
      </Alert>
    )
  }

  // §7.3 3단계 적체 제어
  const lag = (room.lagMs / 1000).toFixed(1)

  if (room.level >= 3) {
    return (
      <Alert variant="destructive">
        <Trash2 className="h-4 w-4" />
        <AlertTitle>적체 L3 — 오래된 발화를 버리는 중 ({lag}초 지연)</AlertTitle>
        <AlertDescription>
          따라잡기 위해 가장 오래된 발화부터 드롭하고 있습니다. 버려진 구간은 자막에
          &ldquo;자막 누락&rdquo; 표시로 남습니다. 잠시 천천히 말씀해 주세요.
        </AlertDescription>
      </Alert>
    )
  }

  if (room.level === 2) {
    return (
      <Alert variant="warning">
        <Layers className="h-4 w-4" />
        <AlertTitle>적체 L2 — 발화를 병합하는 중 ({lag}초 지연)</AlertTitle>
        <AlertDescription>
          인접한 발화를 합쳐 호출 수를 줄이고 있습니다. 자막이 조금 뭉쳐 나올 수 있습니다.
        </AlertDescription>
      </Alert>
    )
  }

  if (room.level === 1) {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>적체 L1 — 따라잡는 중 ({lag}초 지연)</AlertTitle>
        <AlertDescription>
          코치 분석을 멈추고 통역에 자원을 집중합니다. 실시간 미리보기 없이 확정 자막만
          나옵니다.
        </AlertDescription>
      </Alert>
    )
  }

  return null
}
